import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useAuth } from "../../../../../context/AuthContext";
import { EXPRESS_BASE_URL } from "../../../../../Helpers/apiConfig";

const API = `${EXPRESS_BASE_URL}/api/ai-settings`;

// Express'ten gelen hata mesajını okunur hale getirir (403 = admin değil, 503 = yetki doğrulanamadı)
const errorText = (err) => err.response?.data?.message || err.message || "Bir hata oluştu";

export const AdminAiIntegration = () => {
    const { user } = useAuth();
    const headers = { Authorization: `Bearer ${user?.token}` };

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);

    // Sunucudaki durum (anahtar asla gelmez, sadece son 4 hanesi)
    const [saved, setSaved] = useState(null);
    const [models, setModels] = useState([]);
    const [testResult, setTestResult] = useState(null);

    const [form, setForm] = useState({
        api_key: "",
        model: "gemini-2.5-flash",
        temperature: 0.7,
        max_output_tokens: 2048,
        is_active: false,
        clear_key: false,
    });
    const [showKey, setShowKey] = useState(false);

    const applySaved = (data) => {
        setSaved(data);
        setForm((f) => ({
            ...f,
            api_key: "",
            clear_key: false,
            model: data.model,
            temperature: data.temperature,
            max_output_tokens: data.max_output_tokens,
            is_active: data.is_active,
        }));
    };

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(API, { headers });
                if (res.data.success) applySaved(res.data.data);
            } catch (err) {
                setLoadError(errorText(err));
            }
            setLoading(false);
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const fetchModels = async () => {
        setLoadingModels(true);
        try {
            const res = await axios.post(`${API}/models`, { api_key: form.api_key }, { headers });
            setModels(res.data.data || []);
            if (!res.data.data?.length) Swal.fire("Bilgi", "Bu anahtarla kullanılabilir model bulunamadı.", "info");
        } catch (err) {
            Swal.fire("Hata", errorText(err), "error");
        }
        setLoadingModels(false);
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await axios.post(`${API}/test`, { api_key: form.api_key, model: form.model }, { headers });
            setTestResult({ ok: true, ...res.data.data });
        } catch (err) {
            setTestResult({ ok: false, message: errorText(err) });
        }
        setTesting(false);
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                model: form.model,
                temperature: Number(form.temperature),
                max_output_tokens: Number(form.max_output_tokens),
                is_active: form.is_active,
                clear_key: form.clear_key,
            };
            if (form.api_key.trim()) payload.api_key = form.api_key.trim();
            const res = await axios.put(API, payload, { headers });
            applySaved(res.data.data);
            setShowKey(false);
            Swal.fire("Başarılı", "AI entegrasyon ayarları kaydedildi.", "success");
        } catch (err) {
            Swal.fire("Hata", errorText(err), "error");
        }
        setSaving(false);
    };

    // Seçili model listede yoksa (liste henüz çekilmediyse) yine de seçenek olarak göster
    const modelOptions = models.some((m) => m.id === form.model) || !form.model
        ? models
        : [{ id: form.model, name: form.model }, ...models];

    if (loading) {
        return <div className="container-fluid py-4 text-center text-muted">Yükleniyor...</div>;
    }

    if (loadError) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">{loadError}</div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="card shadow-sm border-0 mb-4 p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                        <h3 className="fw-bold text-primary m-0">AI Entegrasyon</h3>
                        <small className="text-muted">Google Gemini bağlantı ayarları</small>
                    </div>
                    <span className={`badge fs-6 ${saved?.is_active ? "bg-success" : "bg-secondary"}`}>
                        {saved?.is_active ? "Aktif" : "Pasif"}
                    </span>
                </div>
            </div>

            <form onSubmit={save}>
                <div className="row g-4">
                    <div className="col-lg-7">
                        <div className="card shadow-sm border-0 h-100">
                            <div className="card-body">
                                <h5 className="fw-bold mb-3">Bağlantı</h5>

                                <label className="form-label fw-semibold">Gemini API Anahtarı</label>
                                {saved?.has_key && !form.clear_key && (
                                    <div className={`small mb-2 ${saved.key_readable ? "text-success" : "text-danger"}`}>
                                        {saved.key_readable
                                            ? <>Kayıtlı anahtar: <code>••••••••{saved.key_hint}</code> — değiştirmek için yenisini girin, boş bırakırsanız korunur.</>
                                            : "Kayıtlı anahtar çözülemiyor (sunucudaki AI_SECRET değişmiş olabilir). Lütfen anahtarı yeniden girin."}
                                    </div>
                                )}
                                <div className="input-group mb-2">
                                    <input
                                        type={showKey ? "text" : "password"}
                                        className="form-control"
                                        placeholder={saved?.has_key ? "Yeni anahtar (opsiyonel)" : "AIza..."}
                                        value={form.api_key}
                                        onChange={(e) => setField("api_key", e.target.value)}
                                        autoComplete="off"
                                        spellCheck={false}
                                        disabled={form.clear_key}
                                    />
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowKey((s) => !s)}>
                                        {showKey ? "Gizle" : "Göster"}
                                    </button>
                                </div>
                                {saved?.has_key && (
                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="clearKey"
                                            checked={form.clear_key}
                                            onChange={(e) => setForm((f) => ({ ...f, clear_key: e.target.checked, api_key: "", is_active: e.target.checked ? false : f.is_active }))}
                                        />
                                        <label className="form-check-label text-danger" htmlFor="clearKey">Kayıtlı anahtarı sil</label>
                                    </div>
                                )}
                                <div className="small text-muted mb-4">
                                    Anahtarı <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer">Google AI Studio</a>'dan alabilirsiniz.
                                    Anahtar sunucuda şifreli saklanır ve bu sayfaya bir daha gönderilmez.
                                </div>

                                <label className="form-label fw-semibold">Model</label>
                                <div className="input-group mb-1">
                                    <select className="form-select" value={form.model} onChange={(e) => setField("model", e.target.value)}>
                                        {modelOptions.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name === m.id ? m.id : `${m.name} (${m.id})`}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="btn btn-outline-primary" onClick={fetchModels} disabled={loadingModels || form.clear_key}>
                                        {loadingModels ? "Getiriliyor..." : "Modelleri getir"}
                                    </button>
                                </div>
                                <div className="small text-muted mb-4">Liste, girdiğiniz (ya da kayıtlı) anahtarla Google'dan güncel olarak çekilir.</div>

                                <div className="row g-3">
                                    <div className="col-sm-7">
                                        <label className="form-label fw-semibold">
                                            Temperature: <span className="text-primary">{Number(form.temperature).toFixed(1)}</span>
                                        </label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="0"
                                            max="2"
                                            step="0.1"
                                            value={form.temperature}
                                            onChange={(e) => setField("temperature", e.target.value)}
                                        />
                                        <div className="d-flex justify-content-between small text-muted">
                                            <span>Tutarlı</span><span>Yaratıcı</span>
                                        </div>
                                    </div>
                                    <div className="col-sm-5">
                                        <label className="form-label fw-semibold">Maks. çıktı token</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min="1"
                                            max="65536"
                                            value={form.max_output_tokens}
                                            onChange={(e) => setField("max_output_tokens", e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-5">
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-body">
                                <h5 className="fw-bold mb-3">Durum</h5>
                                <div className="form-check form-switch mb-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="isActive"
                                        checked={form.is_active}
                                        onChange={(e) => setField("is_active", e.target.checked)}
                                        disabled={form.clear_key}
                                    />
                                    <label className="form-check-label" htmlFor="isActive">AI entegrasyonu aktif</label>
                                </div>
                                <div className="small text-muted">Pasifken AI ile soru üretimi gibi özellikler çalışmaz.</div>
                                {saved?.updated_at && (
                                    <div className="small text-muted mt-3">
                                        Son güncelleme: {new Date(saved.updated_at).toLocaleString("tr-TR")}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-body">
                                <h5 className="fw-bold mb-3">Bağlantı Testi</h5>
                                <p className="small text-muted">
                                    Seçili modele kısa bir istek gönderir. Girdiğiniz anahtar henüz kaydedilmemiş olsa bile onunla test edilir.
                                </p>
                                <button type="button" className="btn btn-outline-success w-100" onClick={testConnection} disabled={testing || form.clear_key}>
                                    {testing ? "Test ediliyor..." : "Bağlantıyı Test Et"}
                                </button>
                                {testResult && (
                                    <div className={`alert mt-3 mb-0 small ${testResult.ok ? "alert-success" : "alert-danger"}`}>
                                        {testResult.ok ? (
                                            <>
                                                <div><strong>Başarılı</strong> — {testResult.model}, {testResult.ms} ms</div>
                                                <div className="text-muted">Yanıt: {testResult.reply || "(boş)"}</div>
                                            </>
                                        ) : (
                                            <><strong>Başarısız:</strong> {testResult.message}</>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="text-end mt-4">
                    <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                        {saving ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminAiIntegration;
